import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { USA_STATES } from "@src/appV2/lib/utils";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useGetQualifications } from "@src/appV2/Qualifications/api/useGetQualifications";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isEmpty } from "lodash";
import { type ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";
import { z } from "zod";

import { useAddLicense } from "../../Licenses/api/useAddLicense";
import { AddSsnBottomSheet } from "../../Ssn/components/AddSsnBottomSheet";
import { useIsSsnRequired } from "../../Ssn/hooks/useIsSsnRequired";
import { useCreateShoppableQualification } from "../api/useCreateShoppableQualification";
import { type AddLicenseSearchParams } from "./types";
import {
  type AddLicenseFormFieldBehavior,
  useAddLicenseFormFieldBehavior,
} from "./useFormFieldBehavior";

// Dynamic schema creator based on feature flags
const createAddLicenseSchema = (fieldBehavior?: AddLicenseFormFieldBehavior) => {
  const { state, licenseNumber } = fieldBehavior ?? {};
  const validStateNames = new Set(USA_STATES.map((state) => state.name));

  return z.object({
    qualificationName: z.string().min(1, "License type is required"),
    state: state?.hidden
      ? z.string().optional()
      : z
          .string()
          .min(1, "State is required")
          .refine((value) => validStateNames.has(value), {
            message: "Please select a valid state",
          }),
    licenseNumber:
      licenseNumber?.hidden ?? !licenseNumber?.required
        ? z.string().optional()
        : z.string().min(1, "License number is required"),
  });
};

export type AddLicenseFormData = z.infer<ReturnType<typeof createAddLicenseSchema>>;

interface AddLicenseFormProviderProps {
  children: ReactNode;
  qualificationId: string;
  inReviewPath: string;
  duplicatePath: string;
  errorPath: string;
  submittingPath: string;
}

export function AddLicenseFormProvider(props: AddLicenseFormProviderProps) {
  const { children, qualificationId, inReviewPath, duplicatePath, errorPath, submittingPath } =
    props;
  const history = useHistory();
  const worker = useDefinedWorker();
  const { mutateAsync: addLicense } = useAddLicense(worker.userId);
  const { mutateAsync: createShoppableQualification } = useCreateShoppableQualification();
  const { checkIsSsnRequired } = useIsSsnRequired();

  const { state: stateFromUrl } = useSearchParams<AddLicenseSearchParams>();

  const initialState = stateFromUrl ?? worker.address?.state ?? "";

  const [currentSelectedState, setCurrentSelectedState] = useState<string>(initialState);

  const addSsnModalState = useModalState();

  const { data: qualificationsData } = useGetQualifications({
    enabled: true,
  });

  const fieldBehavior = useAddLicenseFormFieldBehavior({
    currentQualification: qualificationsData?.data.find(
      (qualification) => qualification.id === qualificationId
    ),
    selectedState: currentSelectedState,
  });

  const methods = useForm<AddLicenseFormData>({
    resolver: zodResolver(createAddLicenseSchema(fieldBehavior)),
    defaultValues: {
      qualificationName: "",
      state: initialState,
      licenseNumber: "",
    },
    mode: "onChange",
  });

  const stateWatch = methods.watch("state");

  useEffect(() => {
    if (isDefined(stateWatch)) {
      setCurrentSelectedState(stateWatch);
    }
  }, [stateWatch]);

  const onSubmit = async (data: AddLicenseFormData) => {
    history.replace(generatePath(submittingPath, { qualificationId }));

    const currentQualification = qualificationsData?.data.find(
      (qualification) => qualification.id === qualificationId
    );

    try {
      const doesRoleAlreadyExist = worker.shoppableQualifications.find(
        (qualification) => qualification.relationships.qualification.data.id === qualificationId
      );

      if (!doesRoleAlreadyExist) {
        await createShoppableQualification({
          workerId: worker.userId,
          qualificationId,
        });
      }

      const response = await addLicense({
        qualification: currentQualification?.attributes.name ?? "",
        multiState: false,
        state: isEmpty(data.state) || !isDefined(data.state) ? "Any" : data.state,
        number: data.licenseNumber ?? "",
      });

      if (response.duplicate) {
        history.replace(generatePath(duplicatePath, { qualificationId }));
        return;
      }

      history.replace(generatePath(inReviewPath, { qualificationId }));
    } catch {
      history.replace(generatePath(errorPath, { qualificationId }));
    }
  };

  async function onSubmitWithSsnCheck(data: AddLicenseFormData) {
    const isSsnRequired = checkIsSsnRequired(data.state);

    if (isSsnRequired) {
      addSsnModalState.openModal();
      return;
    }

    await onSubmit(data);
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmitWithSsnCheck)}>{children}</form>
      <AddSsnBottomSheet
        modalState={addSsnModalState}
        onSsnAdded={methods.handleSubmit(onSubmit)}
      />
    </FormProvider>
  );
}
